import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { EditableField } from '@/types/editableField';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import {
  DocumentMetadataResponse,
  DocumentsSliceState,
  FileToDownload,
  GetDocumentListPayload,
  GetDocumentPayload,
  GetDocumentsResponse,
  StatusHistoryData,
} from '@/domains/documents/store/types/DocumentsSlice';

const initialState: DocumentsSliceState = {
  data: {
    listings: [],
    pagination: {
      numberOfItems: 0,
      first: '1',
      last: undefined,
      previous: '0',
      next: '0',
      page: 1,
      perPage: 20,
    },
  },
  statuses: {
    isLoading: false,
    isError: false,
    areDocumentVersionsLoading: false,
    isDocumentStatusHistoryLoading: false,
    isDocumentWithCorrespondencesLoading: false,
    isCorrespondenceStatusHistoryLoading: false,
    isMetadataLoading: false,
  },
  documentVersions: null,
  documentStatusHistory: [],
  correspondenceStatusHistory: [],
  documentWithCorrespondences: null,
  documentMetadata: {
    metadata: [],
  },
};

const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    getDocument: (_, _action: PayloadAction<GetDocumentPayload>) => {},
    getDocumentList: (_, _action: PayloadAction<GetDocumentListPayload>) => {},
    getDocumentSuccess: (state, action: PayloadAction<NormalizedV2Response>) => {
      state.data.listings = state.data.listings.map((document) =>
        document.id === action.payload.id ? action.payload : document,
      );
    },
    getDocumentWithCorrespondence: (_, _action: PayloadAction<GetDocumentPayload>) => {},
    getDocumentWithCorrespondenceSuccess: (state, action: PayloadAction<NormalizedV2Response>) => {
      state.documentWithCorrespondences = action.payload;
    },
    incrementDocumentsPage: (state, _action: PayloadAction<GetDocumentListPayload>) => {
      state.data.pagination.page++;
    },
    getDocumentListSuccess: (
      state,
      {
        payload: { shouldRewrite = true, data },
      }: PayloadAction<{ shouldRewrite?: boolean; data: GetDocumentsResponse }>,
    ) => {
      const listingsArray = Array.isArray(data.listings) ? data.listings : [data.listings];

      if (shouldRewrite) {
        state.data.listings = [...listingsArray];
        state.data.pagination = data.pagination;
      } else {
        state.data.listings = [...state.data.listings, ...listingsArray];
        state.data.pagination = data.pagination;
      }

      state.statuses.isLoading = false;
    },
    startLoadingDocumentListData: (state) => {
      state.statuses.isLoading = true;
    },
    getDocumentListDataError: (state) => {
      state.statuses.isLoading = false;
      state.statuses.isError = true;
    },
    updateDocumentField: (state, action: PayloadAction<{ id: number; editedFields: EditableField[] }>) => {
      const document = state.data.listings.find((document) => document.id === action.payload.id);
      if (!document) {
        return state;
      }

      let updatedDocumentFields = { ...document.fields };

      action.payload.editedFields.forEach((editedField) => {
        const updatedDocumentField = Object.fromEntries(
          Object.entries(document.fields)
            .filter(
              ([documentFieldName, _]) => documentFieldName.toLowerCase() === editedField.entityField.toLowerCase(),
            )
            .map(([documentFieldName, documentFieldValue]) => [
              documentFieldName,
              { ...documentFieldValue, value: editedField.value },
            ]),
        );
        // @ts-expect-error field now can be undefined, TODO: fix
        updatedDocumentFields = { ...updatedDocumentFields, ...updatedDocumentField };
      });

      document.fields = updatedDocumentFields;
    },
    resetDocumentsSlice: () => initialState,
    resetDocumentWithCorrespondences: (state) => {
      state.documentWithCorrespondences = initialState.documentWithCorrespondences;
    },
    downloadDocument: (_, _action: PayloadAction<FileToDownload>) => {},

    getDocumentEntityVersions: (_, _action: PayloadAction<number>) => {},
    getDocumentEntityVersionsSuccess: (state, action: PayloadAction<NormalizedV2Response[]>) => {
      state.documentVersions = action.payload;
    },
    setDocumentVersionsLoading(state, action: PayloadAction<boolean>) {
      state.statuses.areDocumentVersionsLoading = action.payload;
    },
    getDocumentStatusHistory: (_, _action: PayloadAction<number>) => {},
    getDocumentStatusHistorySuccess: (state, action: PayloadAction<StatusHistoryData[]>) => {
      state.documentStatusHistory = action.payload;
    },
    setDocumentStatusHistoryLoading(state, action: PayloadAction<boolean>) {
      state.statuses.isDocumentStatusHistoryLoading = action.payload;
    },
    getCorrespondenceStatusHistory: (_, _action: PayloadAction<number>) => {},
    getCorrespondenceStatusHistorySuccess: (state, action: PayloadAction<StatusHistoryData[]>) => {
      state.correspondenceStatusHistory = action.payload;
    },
    setDocumentWithCorrespondencesLoading(state, action: PayloadAction<boolean>) {
      state.statuses.isDocumentWithCorrespondencesLoading = action.payload;
    },
    setCorrespondenceStatusHistoryLoading(state, action: PayloadAction<boolean>) {
      state.statuses.isCorrespondenceStatusHistoryLoading = action.payload;
    },
    getDocumentMetadata: (_, _action: PayloadAction<string>) => {},
    getDocumentMetadataSuccess: (state, action: PayloadAction<DocumentMetadataResponse>) => {
      state.documentMetadata = action.payload;
    },
    resetDocumentMetadata: (state) => {
      state.documentMetadata = initialState.documentMetadata;
    },
    setDocumentMetadataLoading(state, action: PayloadAction<boolean>) {
      state.statuses.isMetadataLoading = action.payload;
    },
  },
});

export const {
  getDocument,
  getDocumentList,
  getDocumentSuccess,
  getDocumentListSuccess,
  getDocumentWithCorrespondence,
  getDocumentWithCorrespondenceSuccess,
  resetDocumentWithCorrespondences,
  startLoadingDocumentListData,
  getDocumentListDataError,
  resetDocumentsSlice,
  updateDocumentField,
  incrementDocumentsPage,
  downloadDocument,
  getDocumentEntityVersions,
  getDocumentEntityVersionsSuccess,
  setDocumentVersionsLoading,
  getDocumentStatusHistory,
  getDocumentStatusHistorySuccess,
  setDocumentStatusHistoryLoading,
  setDocumentWithCorrespondencesLoading,
  getCorrespondenceStatusHistory,
  getCorrespondenceStatusHistorySuccess,
  setCorrespondenceStatusHistoryLoading,
  getDocumentMetadata,
  getDocumentMetadataSuccess,
  setDocumentMetadataLoading,
  resetDocumentMetadata,
} = documentsSlice.actions;

export const systemDocumentsReducer = documentsSlice.reducer;
export const systemDocumentsReducerPath = documentsSlice.reducerPath;
